import {CLSMetric, FIDMetric, LCPMetric, onCLS, onFID, onLCP} from 'web-vitals';
import {AnyRouteResult} from '@/app/interfaces/routing';
import {RouteName} from '@/app/constants/routing';
import {ForecastDaysMode} from '@/app/constants/forecast';
import {sendStats} from '@/common/helpers/stats/sendStats';

// Returns only the largest layout shift entry over the lifecycle of the page view (for CLS metric)
const getLargestLayoutShiftEntry = (entries: CLSMetric['entries']) => {
	return entries.reduce((a, b) => (a && a.value > b.value ? a : b));
};

// Returns only the largest layout shift source within that entry (for CLS metric)
const getLargestLayoutShiftSource = (sources: CLSMetric['entries'][number]['sources']) => {
	return sources.reduce((a, b) => {
		return a.node && a.previousRect.width * a.previousRect.height > b.previousRect.width * b.previousRect.height
			? a
			: b;
	});
};

export function webVitalsStatsReport(route: AnyRouteResult, interfaceName: 'desktop' | 'mobile') {
	const getPageType = () => {
		if (route.name === RouteName.mainPage) {
			return 'main-page';
		} else if (route.name === RouteName.map) {
			return 'map';
		} else if (route.name === RouteName.weather && route.data.daysMode === ForecastDaysMode.Days7) {
			return 'weather-7days';
		} else if (route.name === RouteName.weather && route.data.daysMode === ForecastDaysMode.Days10) {
			return 'weather-10days';
		}
		return '';
	};

	const page = getPageType();

	const sendCLSReport = (metric: CLSMetric) => {
		if (!metric.entries.length) {
			return;
		}

		const largestEntry = getLargestLayoutShiftEntry(metric.entries);
		// Report only on the largest LayoutShift entry, and the largest LayoutShiftSource within it
		if (largestEntry && largestEntry.sources && largestEntry.sources.length) {
			const largestSource = getLargestLayoutShiftSource(largestEntry.sources);
			if (largestSource) {
				sendStats(`pperf/${interfaceName}/${page}/cls/${largestEntry.value.toFixed(2)}`);
			}
		}
	};

	const sendFIDReport = (metric: FIDMetric) => {
		if (!metric.entries.length) {
			return;
		}
		sendStats(`pperf/${interfaceName}/${page}/fid/${metric.value.toFixed(2)}`);
	};

	const sendLCPReport = (metric: LCPMetric) => {
		if (!metric.entries.length) {
			return;
		}

		const entry = metric.entries[metric.entries.length - 1];
		const element = entry.element;
		if (!element) {
			sendStats(`pperf/${interfaceName}/${page}/lcp/${entry.renderTime.toFixed(2)}`);
			return;
		}

		// Returns the selector as a string, (id in the format '#id', element with class in the format 'element.class')
		const selector = element.id ? `#${element.id}` : `${element.nodeName.toLowerCase()}.${element.className}`;
		sendStats(`pperf/${interfaceName}/${page}/lcp/${entry.renderTime.toFixed(2)}`, {selector});
	};

	onLCP(sendLCPReport, {reportAllChanges: true});
	onCLS(sendCLSReport, {reportAllChanges: true});
	onFID(sendFIDReport, {reportAllChanges: true});
}
